import {IConflict} from "external-services/bookings-conflicts-service/bookingConflicts.types";
import DateUtilsService from "external-services/date-utils-service";
import {IFloorplanTableCalculated, IProgressTableState} from "floorplan-v2/app/services/PositionService/types";

const NS = 'FloorplanConflictsService';
class FloorplanConflictsService {

  /**
   * Returns true if a table's conflicts intersect with the selected time or an upcoming time.
   * @param table - table with properties:
   *    - conflicts: list of conflicts, each with a 1st and 2nd booking that overlap
   *    - bookingsForSelectedTime: list of bookings that are active at a selected time
   *    - upcomingBookings: list of bookings that will start soon after the selected time
   * @param selectedFloorplanTime - time selected in the Floorplan Header
   */
  isConflict(table: IFloorplanTableCalculated, selectedFloorplanTime: Date) {
    if (table.conflicts?.length) {
      if (table.bookingsForSelectedTime.length) {// should check current booking whether conflict
        const ids = table.bookingsForSelectedTime.map((b) => b._id);
        return table.conflicts.some((c) => {
          if (ids.includes(c.firstBooking._id)) {
            return c.secondBooking.bookingEnd > selectedFloorplanTime;
          }
          if (ids.includes(c.secondBooking._id)) {
            return c.firstBooking.bookingEnd > selectedFloorplanTime;
          }
          return false;
        });
      } else if (table.upcomingBookings.length) {// should check upcoming booking whether conflict
        const ids = table.upcomingBookings.map((b) => b._id);
        return table.conflicts.some((c) => {
          return ids.includes(c.firstBooking._id) || ids.includes(c.secondBooking._id);
        });
      }
    }
    return false;
  }

  /**
   * Filters a list of conflicts for a specified table and time (+ 90 minutes duration), with additional maxValue/value properties
   * @param tableId - only return conflicts for this table
   * @param conflicts - list of conflicts
   * @param selectedDate - time to check from, with + 90 minutes duration
   * @return filtered list of conflicts, with additional maxValue/value properties
   */
  getConflictsAtTime(
    tableId: string,
    conflicts: IConflict[],
    selectedDate: Date
  ): (IConflict & IProgressTableState)[] {
    const tempConflicts: (IConflict & IProgressTableState)[] = [];

    conflicts.forEach((c) => {
      if (c.table._id === tableId) {
        const conflictAtCurrentTime = this.isConflictAtSelectedTime(c, selectedDate);
        if (conflictAtCurrentTime.isAt) {
          tempConflicts.push({
            ...c,
            maxValue: conflictAtCurrentTime.maxValue,
            value: conflictAtCurrentTime.value,
          });
        }
      }
    });

    return tempConflicts;
  }

  private isConflictAtSelectedTime(conflict: IConflict, selectedDate: Date): {
    isAt: boolean;
    maxValue: number;
    value: number;
  } {
    if (!conflict || !selectedDate) return { isAt: false, maxValue: 0, value: 0 };
    const { firstBooking, secondBooking } = conflict;

    const firstBookingStartTime = new Date(firstBooking.time).getTime();
    const secondBookingStartTime = new Date(secondBooking.time).getTime();
    const firstBookingEndTime = new Date(firstBooking.bookingEnd).getTime();
    const secondBookingEndTime = new Date(secondBooking.bookingEnd).getTime();

    const conflictStartTime = Math.min(firstBookingStartTime, secondBookingStartTime);
    const conflictEndTime = Math.max(firstBookingEndTime, secondBookingEndTime);
    const selectedTime = selectedDate.getTime();
    const ninetyMinutesLater = selectedTime + 90 * 60 * 1000;

    const isAt = DateUtilsService.doPeriodsInteract(conflictStartTime, conflictEndTime, selectedTime, ninetyMinutesLater);

    let maxValue: number = 0,
      value: number = 0;
    if (isAt) {
      maxValue = conflictEndTime - conflictStartTime;
      // give value 1 to make process bar show;
      value = selectedTime === conflictStartTime ? 1 : selectedTime < conflictStartTime ? 0 : selectedTime - conflictStartTime;
    }
    return {
      isAt,
      maxValue,
      value,
    };
  }
}

const instance = new FloorplanConflictsService();
export default instance as FloorplanConflictsService;
